import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface GpsPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialLat?: number;
  initialLng?: number;
  onConfirm: (lat: number, lng: number) => void;
}

const MONTREAL_CENTER = { lat: 45.512329, lng: -73.677630 };

const containerStyle = {
  width: '100%',
  height: '400px',
};

export function GpsPickerModal({
  open,
  onOpenChange,
  initialLat,
  initialLng,
  onConfirm,
}: GpsPickerModalProps) {
  const { t } = useTranslation('locations');

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  });

  const hasInitialCoords = initialLat != null && initialLng != null;
  const defaultCenter = hasInitialCoords
    ? { lat: initialLat!, lng: initialLng! }
    : MONTREAL_CENTER;

  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>(defaultCenter);
  const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('roadmap');

  // Reset position when modal opens with new initial coords
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setMarkerPosition(defaultCenter);
    }
    onOpenChange(isOpen);
  };

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setMarkerPosition({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    }
  }, []);

  const handleMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setMarkerPosition({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    }
  }, []);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMarkerPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        toast.error('Unable to get your current location');
      }
    );
  };

  const handleConfirm = () => {
    onConfirm(markerPosition.lat, markerPosition.lng);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('form.gpsPickerTitle', 'Pick Location on Map')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {!apiKey ? (
            <div className="h-[400px] flex items-center justify-center bg-muted rounded-lg text-muted-foreground text-sm">
              Google Maps API key not configured (VITE_GOOGLE_MAPS_API_KEY)
            </div>
          ) : loadError ? (
            <div className="h-[400px] flex items-center justify-center bg-muted rounded-lg text-red-500 text-sm">
              Failed to load Google Maps
            </div>
          ) : !isLoaded ? (
            <div className="h-[400px] flex items-center justify-center bg-muted rounded-lg">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden border">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={markerPosition}
                zoom={hasInitialCoords ? 15 : 11}
                onClick={handleMapClick}
                mapTypeId={mapType}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker
                  position={markerPosition}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
              </GoogleMap>
            </div>
          )}

          {/* Coordinate display + controls */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <MapPin className="w-3 h-3 inline mr-1" />
              {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMapType(mapType === 'roadmap' ? 'satellite' : 'roadmap')}
              >
                {mapType === 'roadmap'
                  ? t('form.satelliteView', 'Satellite')
                  : t('form.mapView', 'Map')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleUseCurrentLocation}
              >
                <MapPin className="h-4 w-4 mr-1" />
                {t('form.useCurrentLocation', 'Current Location')}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('edit.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirm}>
            {t('form.gpsPickerConfirm', 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
